import React, { useMemo } from "react";
import styled, { keyframes } from "styled-components/macro";
import { theme } from "./theme";

const LOW_TONER_THRESHOLD = 20;

// Exact colors from the "TULU Print Admin" design mockup, kept literal (rather
// than mapped onto theme.* tokens) so this card matches the reference pixel-for-
// pixel — the mockup's own palette is close to but not identical to theme.js's.
const PSTATUS = {
  online: { label: "Online", bg: "#EAF7EE", fg: "#2C6B3F", dot: "#34A853", iconBg: "#F3EBFF", iconFg: "#9747FF" },
  busy: { label: "Printing", bg: "#F3EBFF", fg: "#5A1FA8", dot: "#9747FF", iconBg: "#F3EBFF", iconFg: "#740AFF" },
  lowToner: { label: "Low toner", bg: "#FFF6E4", fg: "#8A5A08", dot: "#E9A650", iconBg: "#FFF6E4", iconFg: "#8A5A08" },
  offline: { label: "Offline", bg: "#F3F0FA", fg: "#6B6580", dot: "#BFBFBF", iconBg: "#F3F0FA", iconFg: "#9A94AE" },
};

// Each toner's own real color — not a generic percent-based traffic light —
// so Black/Pink/Yellow/Blue stay visually distinct at a glance.
const TONER_COLOR = {
  Black: "#1F1F1F",
  Pink: "#E91E8C",
  Yellow: "#E8C93A",
  Blue: "#29C4E0",
};

function isSameCalendarDay(date, reference) {
  return (
    date.getFullYear() === reference.getFullYear() &&
    date.getMonth() === reference.getMonth() &&
    date.getDate() === reference.getDate()
  );
}

const PrintCard = ({ printer, jobs, onClick, delay = 0 }) => {
  const printerId = printer.printer_id || printer.id;
  const black = printer.Black ?? printer.tonerBlack ?? 0;
  const pink = printer.Pink ?? printer.tonerPink ?? 0;
  const yellow = printer.Yellow ?? printer.tonerYellow ?? 0;
  const blue = printer.Blue ?? printer.tonerBlue ?? 0;
  const lowestToner = Math.min(black, pink, yellow, blue);

  // Approximate — based only on the jobs currently loaded in the dashboard snapshot,
  // not a true full-day server aggregate.
  const { inQueue, today, isPrintingNow } = useMemo(() => {
    const now = new Date();
    let queueCount = 0;
    let todayCount = 0;
    let printingNow = false;
    (jobs || []).forEach((job) => {
      if (job.printer_id !== printerId) return;
      if (job.status === "wait") queueCount += 1;
      if (job.status === "printing") printingNow = true;
      const jobDate = job.unix instanceof Date ? job.unix : new Date(job.unix || 0);
      if (isSameCalendarDay(jobDate, now)) todayCount += 1;
    });
    return { inQueue: queueCount, today: todayCount, isPrintingNow: printingNow };
  }, [jobs, printerId]);

  // printer.status is the RPI's own rich diagnostic string (e.g. "Troubleshoot
  // Failed - Test Print Failed", "Paper-jam", "No Paper" — not just OK/error).
  // hasIssue is computed server-side (v_printer.ts) from the same authoritative
  // rule the RPI/server itself uses, so a handful of non-"OK" statuses that are
  // NOT real problems (e.g. "No Paper", "Troubleshooting...") don't wrongly show
  // as "Offline" here.
  const hasIssue = printer.hasIssue ?? (printer.status !== "OK" && printer.status !== "ok");
  const statusKey = hasIssue
    ? "offline"
    : isPrintingNow
    ? "busy"
    : lowestToner < LOW_TONER_THRESHOLD
    ? "lowToner"
    : "online";
  const status = PSTATUS[statusKey];

  const primaryName = printer.room_name || printer.title || "Printer";
  const subtitle = printer.title && printer.title !== primaryName ? printer.title : null;

  return (
    <Wrapper onClick={onClick} delay={delay}>
      <HeaderRow>
        <IconBadge iconBg={status.iconBg} iconFg={status.iconFg}>⎙</IconBadge>
        <NameBlock>
          <Name>{primaryName}</Name>
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
        </NameBlock>
        <StatusPill pillBg={status.bg} pillFg={status.fg}>
          <StatusDot dotColor={status.dot} pulse={statusKey === "busy"} />
          {status.label}
        </StatusPill>
      </HeaderRow>

      {hasIssue && printer.status ? <ReasonLine>{printer.status}</ReasonLine> : null}

      <TonerSection>
        <TonerRow label="Black" percent={black} />
        <TonerRow label="Pink" percent={pink} />
        <TonerRow label="Yellow" percent={yellow} />
        <TonerRow label="Blue" percent={blue} />
      </TonerSection>

      <StatsRow>
        <StatBlock>
          <StatValue>{inQueue}</StatValue>
          <StatLabel>in queue</StatLabel>
        </StatBlock>
        <StatBlock>
          <StatValue>{today}</StatValue>
          <StatLabel>jobs today</StatLabel>
        </StatBlock>
      </StatsRow>
    </Wrapper>
  );
};

const TonerRow = ({ label, percent }) => (
  <TonerLine>
    <TonerLabelRow>
      <span>{label}</span>
      <TonerValue low={percent < LOW_TONER_THRESHOLD}>{percent}%</TonerValue>
    </TonerLabelRow>
    <TonerTrack>
      <TonerFill percent={percent} barColor={TONER_COLOR[label]} />
    </TonerTrack>
  </TonerLine>
);

export { PrintCard };

const popIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: none; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(151, 71, 255, 0.45); }
  70% { box-shadow: 0 0 0 7px rgba(151, 71, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(151, 71, 255, 0); }
`;

const Wrapper = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: start;
  border: 1px solid #ede9f6;
  border-radius: 18px;
  padding: 20px;
  cursor: pointer;
  background-color: #ffffff;
  outline: none;
  font-family: ${theme.fontFamily};
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 220ms ease, border-color 220ms ease;
  animation: ${popIn} 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: ${({ delay }) => delay}ms;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(28, 28, 97, 0.1);
    border-color: #dccdf7;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 13px;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  background: ${({ iconBg }) => iconBg};
  color: ${({ iconFg }) => iconFg};
`;

const NameBlock = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: ${theme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subtitle = styled.div`
  font-size: 11.5px;
  color: #a09ab2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusPill = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ pillBg }) => pillBg};
  color: ${({ pillFg }) => pillFg};
`;

const StatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ dotColor }) => dotColor};
  animation: ${({ pulse }) => (pulse ? pulseRing : "none")} 1.8s ease-out infinite;
`;

const TonerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const TonerLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TonerLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11.5px;
  color: #8b85a0;
`;

const TonerValue = styled.span`
  font-weight: 600;
  color: ${({ low }) => (low ? "#E05353" : "#4a4a4a")};
`;

const ReasonLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: -8px;
  font-size: 11.5px;
  font-weight: 500;
  color: #e05353;
`;

const TonerTrack = styled.div`
  height: 6px;
  border-radius: 20px;
  background: #f1edf9;
  overflow: hidden;
`;

const TonerFill = styled.div`
  height: 100%;
  border-radius: 20px;
  width: ${({ percent }) => Math.max(0, Math.min(100, percent))}%;
  background: ${({ barColor }) => barColor};
  transition: width 1s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const StatsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const StatBlock = styled.div`
  flex: 1;
  background: #faf8fe;
  border-radius: 11px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1;
  color: ${theme.text};
`;

const StatLabel = styled.div`
  font-size: 10.5px;
  color: #a09ab2;
`;
